import { fetchCampaignActivity, getVersionAgentPagination } from "@/api/campaign";
import { fetchUpdateHistory } from "@/api/updateHistory";
import { useClientStatisticsQuery } from "@/composables/clients";
import { useQuery } from "@tanstack/vue-query";
import { computed, ref } from "vue";

// Activity window in days; 0 = all time.
export type ActivityPeriod = 0 | 1 | 7 | 30;

export interface CampaignActivityBucket {
  type: string;
  count: number;
}

export interface UpdateStatusBucket {
  status: string;
  count: number;
}

export function useCampaignDashboard(campaignId: string) {
  // Agents online/offline/uninstalled for this campaign (GET /view/agent/stat?campaign=)
  const agents = useClientStatisticsQuery(campaignId);

  // Recent update actions for this campaign (POST /view/action/history/update-agent)
  const updateHistory = useQuery({
    queryKey: ["campaignDashboardUpdateHistory", campaignId],
    queryFn: () =>
      fetchUpdateHistory({
        page: 1,
        size: 100,
        campaign_id: campaignId,
        agent_id: "",
      }),
    staleTime: 60 * 1000,
  });

  // Installers registered for this campaign (POST /view/version-agent/pagination)
  const installers = useQuery({
    queryKey: ["campaignDashboardInstallers", campaignId],
    queryFn: () =>
      getVersionAgentPagination({ page: 1, size: 100, campaign: campaignId }),
    staleTime: 60 * 1000,
  });

  // Activity window selector (0 = all time); drives the activity query key.
  const activityPeriod = ref<ActivityPeriod>(0);

  // Action-scoped activity for this campaign: totals, per-type breakdown,
  // screenshots, and daily trend (GET /view/campaign/activity/:id)
  const activity = useQuery({
    queryKey: ["campaignDashboardActivity", campaignId, activityPeriod],
    queryFn: () =>
      fetchCampaignActivity(campaignId, activityPeriod.value || undefined),
    staleTime: 60 * 1000,
  });

  const agentTotal = computed(
    () => agents.data.value?.reduce((a, c) => a + c.total, 0) ?? 0,
  );
  const agentOnline = computed(
    () => agents.data.value?.find((s) => s._id === "online")?.total ?? 0,
  );
  const agentOffline = computed(
    () => agents.data.value?.find((s) => s._id === "offline")?.total ?? 0,
  );
  const agentUninstalled = computed(
    () =>
      agents.data.value
        ?.flatMap((s) => s.items ?? [])
        .filter((item) => item.uninstalled)
        .reduce((a, c) => a + c.count, 0) ?? 0,
  );
  const onlinePercent = computed(() =>
    agentTotal.value > 0
      ? Math.round((agentOnline.value / agentTotal.value) * 100)
      : 0,
  );

  const updateTotal = computed(
    () => updateHistory.data.value?.total_filter ?? 0,
  );
  const updateStatusBuckets = computed<UpdateStatusBucket[]>(() => {
    const counts = new Map<string, number>();
    for (const item of updateHistory.data.value?.data ?? []) {
      const key = item.status || "unknown";
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return [...counts.entries()]
      .map(([status, count]) => ({ status, count }))
      .sort((a, b) => b.count - a.count);
  });
  const recentUpdates = computed(() =>
    (updateHistory.data.value?.data ?? []).slice(0, 10),
  );

  const installerTotal = computed(
    () => installers.data.value?.total_filter ?? 0,
  );
  const latestInstaller = computed(() => {
    const items = installers.data.value?.data ?? [];
    if (items.length === 0) return null;
    return [...items].sort((a, b) => b.created_at - a.created_at)[0];
  });

  // --- Activity metrics (screenshots, actions taken, top type, trend) ---
  const totalActions = computed(
    () => activity.data.value?.data?.total_actions ?? 0,
  );
  const screenshotsCount = computed(
    () => activity.data.value?.data?.screenshots ?? 0,
  );
  const actionTypeBuckets = computed<CampaignActivityBucket[]>(() => {
    const byType = activity.data.value?.data?.by_type ?? {};
    return Object.entries(byType)
      .map(([type, count]) => ({ type, count }))
      .sort((a, b) => b.count - a.count);
  });
  const topActionType = computed(() =>
    actionTypeBuckets.value.length > 0
      ? actionTypeBuckets.value[0]
      : undefined,
  );
  const activityTrend = computed(
    () => activity.data.value?.data?.trend ?? [],
  );

  const isLoading = computed(
    () =>
      agents.isLoading.value ||
      updateHistory.isLoading.value ||
      installers.isLoading.value ||
      activity.isLoading.value,
  );
  const isError = computed(
    () =>
      agents.isError.value ||
      updateHistory.isError.value ||
      installers.isError.value ||
      activity.isError.value,
  );

  const refetch = () => {
    void agents.refetch();
    void updateHistory.refetch();
    void installers.refetch();
    void activity.refetch();
  };

  return {
    isLoading,
    isError,
    refetch,
    agentTotal,
    agentOnline,
    agentOffline,
    agentUninstalled,
    onlinePercent,
    updateTotal,
    updateStatusBuckets,
    recentUpdates,
    installerTotal,
    latestInstaller,
    totalActions,
    screenshotsCount,
    actionTypeBuckets,
    topActionType,
    activityTrend,
    activityPeriod,
  };
}
